import {
  WSPanel,
  WSFlexBox,
  WSLoader,
  WSText,
  WSButton,
  WSProgressBar,
  WSIconList,
  IconListItem
} from "@wingspanhq/fe-component-library";
import {
  BulkStatus,
  IBulkCollaboratorBatch,
  IBulkCollaboratorItem,
  IBulkCollaboratorItemCreate,
  IMemberClientFormW9Info
} from "@wingspanhq/payments/dist/interfaces";
import { CompanyStructure } from "@wingspanhq/users/dist/lib/interfaces/company";
import { AxiosError } from "axios";
import { isEmpty } from "lodash";
import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { PreventLeave } from "../../components/PreventLeave/PreventLeave";
import { SetupFlowRouteComponentProps } from "../../components/SetupFlow/SetupFlow";
import { useUserId } from "../../query/hooks/helpers";
import { useUpdateBulkCollaboratorBatch } from "../../query/payments/mutations";
import {
  useBulkCollaboratorBatch,
  useBulkCollaboratorBatchItems,
  useBulkCollaboratorBatchItemsNotYetProcessed,
  useMissingInfoCollaboratorsQuery
} from "../../query/payments/queries";
import { useUpdateActivity } from "../../query/users/mutations";
import { useActivities } from "../../query/users/queries";
import { get1099SetupProgress } from "../../query/users/selectors";
import { paymentsService } from "../../services/payments";
import { bulkActionFactory } from "../../utils/bulkActionFactory";
import { NEC1099FilingContainer } from "../components/NEC1099FilingContainer";
import { ImportFailed } from "./NEC1099FilingUploadOffPlatformPayments";

import styles from "./styles.module.scss";

interface ImportSummaryProps {
  totalFailedBatchItems: number;
  summary: IBulkCollaboratorBatch;
  totalImportedRows: number;
  isUploadStarted?: boolean;
}

export const ImportSummary: React.FC<ImportSummaryProps> = ({
  summary,
  totalFailedBatchItems,
  totalImportedRows,
  isUploadStarted
}) => {
  let importPercent = 0;
  let progressText = "";
  if (totalFailedBatchItems > 0) {
    progressText = `${totalFailedBatchItems} of ${totalImportedRows} collaborators could not be processed`;
  }
  if (isUploadStarted) {
    progressText = `Uploading ${totalImportedRows} collaborators...`;
  }
  if (summary && !isEmpty(summary.statistics)) {
    const { statistics } = summary;
    const totalProcessed =
      Number(statistics?.itemsFailed ?? 0) +
      Number(statistics?.itemsProcessed ?? 0);
    importPercent = (totalProcessed * 100) / totalImportedRows;
    progressText = `Uploading ${totalProcessed} of ${totalImportedRows} collaborators...`;
    if (
      totalProcessed === totalImportedRows &&
      Number(statistics?.itemsFailed ?? 0) > 0
    ) {
      progressText = `${Number(
        statistics?.itemsFailed ?? 0
      )} out of ${totalImportedRows} collaborators could not be imported`;
    } else if (Number(statistics?.itemsFailed ?? 0) === 0) {
      progressText = `${Number(
        statistics?.itemsProcessed ?? 0
      )} collaborators imported`;
    }
  }
  return (
    <>
      <WSText.Heading4 mb="M">Import Summary</WSText.Heading4>
      <WSProgressBar percent={importPercent || 0} text={progressText} />
    </>
  );
};

export interface UploadCollaboratorWithFormW9 {
  email: string;
  contractorId?: string;
  collaboratorId?: string;
  firstName: string;
  lastName: string;
  phoneNumber?: string;

  w9FirstName: string;
  w9LastName: string;
  dob?: Date;

  legalBusinessName?: string;
  companyStructure: CompanyStructure;
  ssn: string;
  ein: string;

  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

const canUpdateFormW9Data = (
  uploadCollaborator: UploadCollaboratorWithFormW9
) => {
  if (
    uploadCollaborator.addressLine1 ||
    uploadCollaborator.ssn ||
    uploadCollaborator.ein
  ) {
    return true;
  }
  return false;
};

const bulkBatchItemsToCSVCollaborators = (
  batchItems: IFailedBulkCollaboratorItemCreate[]
): UploadCollaboratorWithFormW9[] => {
  return batchItems.map(batchItem => {
    const nameParts = (batchItem.firstLastName || "")?.split(" ");
    return {
      firstName: nameParts[0] || "",
      lastName: nameParts[1] || "",
      email: batchItem.email as string,
      contractorId: batchItem.externalId,
      collaboratorId: batchItem.collaboratorId,
      phoneNumber: (batchItem.labels?.phoneNumber as string) || "",

      w9FirstName: batchItem.formW9Data?.firstName as string,
      w9LastName: batchItem.formW9Data?.lastName as string,
      dob: batchItem.formW9Data?.dob,

      legalBusinessName: batchItem.formW9Data?.legalBusinessName,
      companyStructure: batchItem.formW9Data
        ?.companyStructure as CompanyStructure,
      ein: batchItem.formW9Data?.ein as string,
      ssn: batchItem.formW9Data?.ssn as string,

      addressLine1: batchItem.formW9Data?.addressLine1 as string,
      addressLine2: batchItem.formW9Data?.addressLine2,
      city: batchItem.formW9Data?.city as string,
      state: batchItem.formW9Data?.state as string,
      postalCode: batchItem.formW9Data?.postalCode as string,
      country: batchItem.formW9Data?.country as string,

      error: batchItem.error
    };
  });
};

export const csvCollaboratorsToBulkBatchCreateItems = (
  csvCollaborators: UploadCollaboratorWithFormW9[],
  selectedCollaboratorGroupId?: string
): IBulkCollaboratorItemCreate[] => {
  return csvCollaborators.map(csvCollaborator => {
    let formW9Data: IMemberClientFormW9Info;
    formW9Data = {
      firstName: csvCollaborator.w9FirstName as string,
      lastName: csvCollaborator.w9LastName as string,
      dob: csvCollaborator.dob as Date,

      legalBusinessName: csvCollaborator.legalBusinessName as string,
      companyStructure: csvCollaborator.companyStructure as CompanyStructure,
      ein: csvCollaborator.ein as string,
      ssn: csvCollaborator.ssn as string,

      addressLine1: csvCollaborator.addressLine1 as string,
      addressLine2: csvCollaborator.addressLine2 as string,
      city: csvCollaborator.city as string,
      state: csvCollaborator.state as string,
      postalCode: csvCollaborator.postalCode as string,
      country: csvCollaborator.country as string
    };

    return {
      firstLastName:
        csvCollaborator.firstName && csvCollaborator.lastName
          ? `${csvCollaborator.firstName} ${csvCollaborator.lastName}`
          : "",
      email: csvCollaborator.email,
      externalId: csvCollaborator.contractorId,
      collaboratorId: csvCollaborator?.collaboratorId,
      ...(selectedCollaboratorGroupId
        ? { collaboratorGroupId: selectedCollaboratorGroupId }
        : {}),
      ...(canUpdateFormW9Data(csvCollaborator)
        ? { formW9Data: formW9Data }
        : {}),
      ...(csvCollaborator.phoneNumber
        ? {
            labels: {
              phoneNumber: csvCollaborator.phoneNumber
            }
          }
        : {})
    };
  });
};

export interface IFailedBulkCollaboratorItemCreate
  extends IBulkCollaboratorItemCreate {
  error: string;
}

export const NEC1099FilingUploadCollaborators: React.FC<SetupFlowRouteComponentProps> = ({
  onNext
}) => {
  const userId = useUserId();
  const history = useHistory<{
    csvCollaborators?: UploadCollaboratorWithFormW9[];
    selectedCollaboratorGroupId: string;
    selectedCollaboratorGroupName: string;
    bulkCollaboratorBatchId: string;
    fileName?: string;
    flatFileBatchId?: string;
  }>();

  const {
    csvCollaborators = [],
    selectedCollaboratorGroupId,
    selectedCollaboratorGroupName,
    bulkCollaboratorBatchId,
    fileName: uploadFileName,
    flatFileBatchId
  } = history.location.state;

  const [areItemsAddedToBatch, setAreItemsAddedToBatch] = useState(false);
  const [isUploadingBatchItems, setIsUploadingBatchItems] = useState(false);
  const [isBatchUploadStarted, setIsBatchUploadStarted] = useState(false);
  const [isBatchFetchingFailed, setIsBatchFetchingFailed] = useState(false);
  const [skipErrors, setSkipErrors] = useState(false);
  const [successCreateBatchItems, setSuccessCreateBatchItems] = useState<
    IBulkCollaboratorItem[]
  >([]);
  const [failedCreateBatchItems, setFailedCreateBatchItems] = useState<
    IFailedBulkCollaboratorItemCreate[]
  >([]);

  const bulkCollaboratorBatchQuery = useBulkCollaboratorBatch(
    bulkCollaboratorBatchId,
    {
      enabled: isBatchUploadStarted && !isBatchFetchingFailed,
      refetchInterval: 3000
    }
  );
  const bulkCollaboratorBatchItemsQuery = useBulkCollaboratorBatchItems(
    bulkCollaboratorBatchId,
    undefined,
    {
      enabled: isBatchUploadStarted && !isBatchFetchingFailed,
      refetchInterval: 3000
    }
  );
  const bulkCollaboratorBatchItemsNotYetProcessedQuery = useBulkCollaboratorBatchItemsNotYetProcessed(
    bulkCollaboratorBatchId,
    {
      enabled: isBatchUploadStarted && !isBatchFetchingFailed,
      refetchInterval: 3000
    }
  );

  const numBulkCollaboratorBatchItemsNotYetProcessed =
    bulkCollaboratorBatchItemsNotYetProcessedQuery.data?.summary?.listSize;

  const missingInfoCollaboratorsQuery = useMissingInfoCollaboratorsQuery();

  const qActivity = useActivities(userId);

  const [updateActivity, updateActivityMeta] = useUpdateActivity(userId);
  const [
    updateBulkCollaboratorBatch,
    updateBulkCollaboratorBatchMeta
  ] = useUpdateBulkCollaboratorBatch();

  useEffect(() => {
    const createBulkCollaboratorBatchItems = async () => {
      const batchItemsRequests = csvCollaboratorsToBulkBatchCreateItems(
        csvCollaborators,
        selectedCollaboratorGroupId
      );
      await bulkCreateBatchItemCollaborator.run(batchItemsRequests);
      // TODO(@Anesh): Send statistics to analytics
    };
    if (!areItemsAddedToBatch) {
      createBulkCollaboratorBatchItems();
    }
  }, []);

  useEffect(() => {
    // stop polling if get batch fails
    if (bulkCollaboratorBatchQuery.isError) {
      setIsBatchFetchingFailed(true);
    }
    if (
      bulkCollaboratorBatchQuery.data &&
      [BulkStatus.Complete, BulkStatus.Failed].includes(
        bulkCollaboratorBatchQuery.data?.status
      )
    ) {
      setIsBatchUploadStarted(false);
      // refetch missing info collaborators list
      missingInfoCollaboratorsQuery.refetch();
    }
  }, [
    bulkCollaboratorBatchQuery.data?.status,
    bulkCollaboratorBatchQuery.isError
  ]);

  const bulkCreateBatchItemCollaborator = bulkActionFactory({
    onStart: () => {
      setIsUploadingBatchItems(true);
    },
    action: async (data: IBulkCollaboratorItemCreate) => {
      try {
        const response = await paymentsService.bulkCollaborator.batchItem.create(
          bulkCollaboratorBatchId,
          data
        );
        setSuccessCreateBatchItems(successCreateBatchItems => [
          ...successCreateBatchItems,
          {
            ...response
          }
        ]);
        return response;
      } catch (error) {
        setFailedCreateBatchItems(failedCreateBatchItems => [
          ...failedCreateBatchItems,
          {
            ...data,
            error:
              (error as AxiosError)?.response?.data.error ||
              "Internal server error"
          }
        ]);
      }
    },
    onFinish: () => {
      setAreItemsAddedToBatch(true);
      setIsUploadingBatchItems(false);
    }
  });

  const onConfirmImport = async () => {
    await updateBulkCollaboratorBatch(
      {
        id: bulkCollaboratorBatchId,
        data: {
          status: BulkStatus.Pending
        }
      },
      {
        onSuccess: () => {
          // BE will start importing collaborators once we patch
          // BulkPayableBatch to Pending status
          setIsBatchUploadStarted(true);
        }
      }
    );
  };

  const importSuccessRowOnly = () => {
    setSkipErrors(true);
    onConfirmImport();
  };

  const onContinue = useCallback(async () => {
    const nec1099SetupProgress = get1099SetupProgress(qActivity.data);
    await updateActivity(
      {
        flows: {
          nec1099Setup: {
            ...nec1099SetupProgress,
            currentStep: 3
          }
        }
      },
      {
        onSuccess: () => {
          onNext({
            doMissingInfoCollaboratorsExists:
              (missingInfoCollaboratorsQuery.data || [])?.length > 0
          });
        }
      }
    );
  }, [missingInfoCollaboratorsQuery.data]);

  const downloadErrorsCSV = () => {
    return {
      filename: `Wingspan_Bulk_Upload_Collaborators_Failed.csv`,
      headers: [
        { label: "Email", key: "email" },
        { label: "Collaborator ID", key: "collaboratorId" },
        { label: "External Collaborator ID", key: "contractorId" }, // externalId

        { label: "First Name", key: "firstName" },
        { label: "Last Name", key: "lastName" },
        { label: "Phone number", key: "phoneNumber" },

        { label: "W-9 First Name", key: "w9FirstName" },
        { label: "W-9 Last Name", key: "w9LastName" },
        { label: "Date of Birth", key: "dob" },
        {
          label: "W-9 Legal Business Name",
          key: "legalBusinessName"
        },
        { label: "W-9 Company Structure", key: "companyStructure" },
        { label: "EIN", key: "ein" },
        { label: "SSN", key: "ssn" },

        { label: "Address Line1", key: "addressLine1" },
        { label: "Address Line2", key: "addressLine2" },
        { label: "City", key: "city" },
        { label: "State", key: "state" },
        { label: "Postal Code", key: "postalCode" },
        { label: "Country", key: "country" },
        { label: "Error", key: "error" }
      ],
      data: bulkBatchItemsToCSVCollaborators(failedCreateBatchItems)
    };
  };
  let totalImportedRows = csvCollaborators.length;
  const bulkCollaboratorBatch = bulkCollaboratorBatchQuery.data as IBulkCollaboratorBatch;
  const createBatchItemsProgress =
    successCreateBatchItems.length + failedCreateBatchItems.length;

  if (skipErrors) {
    totalImportedRows = successCreateBatchItems.length;
  }

  return (
    <NEC1099FilingContainer title="Import Collaborators">
      <PreventLeave
        isEnabled={
          successCreateBatchItems.length > 0 ||
          failedCreateBatchItems.length > 0
        }
        promptMessage="Your collaborators have already been uploaded and are getting processed to be imported. Cancelling now can cause data issues. Are you sure you want to leave the page?"
      />
      {isUploadingBatchItems ? (
        // Show spinner when Bulk batch Items are adding to Bulk Batch
        <WSPanel>
          <WSFlexBox.Center>
            <WSLoader.Spinner />
            <WSText ml="S" color="grey700">
              Processing {createBatchItemsProgress} of {totalImportedRows} rows{" "}
              in spreadsheet...
            </WSText>
          </WSFlexBox.Center>
        </WSPanel>
      ) : failedCreateBatchItems.length > 0 && skipErrors === false ? (
        // handle error view for batch items which failed to add to bulk batch
        <ImportFailed
          downloadErrorsCSV={downloadErrorsCSV}
          totalImportedRows={totalImportedRows}
          totalFailedCreateBatchItems={failedCreateBatchItems.length}
          summary={bulkCollaboratorBatch}
          importCorrectedSpreadsheet={() => {
            history.push({
              pathname: `/member/1099-filing/set-up/upload-failed-collaborators/batch/${bulkCollaboratorBatchId}`,
              state: {
                csvCollaborators,
                selectedCollaboratorGroupId,
                selectedCollaboratorGroupName,
                bulkCollaboratorBatchId,
                fileName: uploadFileName,
                flatFileBatchId
              }
            });
          }}
          importNewSpreadsheet={() => {
            history.push("/member/1099-filing/set-up/import-collaborators");
          }}
          importSuccessRowOnly={importSuccessRowOnly}
          onSkip={onNext}
        />
      ) : (
        // success case - show import preview
        <WSPanel>
          <WSText.Heading3 mb="M">Import Preview</WSText.Heading3>
          <WSIconList
            items={[
              {
                description: `${totalImportedRows} collaborators will be imported.`,
                icon: {
                  name: "personalInfo",
                  color: "blue500",
                  size: "M"
                }
              },
              ...(selectedCollaboratorGroupId
                ? [
                    {
                      description: `${totalImportedRows} collaborators will be added to ${selectedCollaboratorGroupName}.`,
                      icon: {
                        name: "pencil",
                        color: "blue500",
                        size: "M"
                      }
                    } as IconListItem
                  ]
                : [])
            ]}
            mb="M"
          />

          <ImportSummary
            summary={bulkCollaboratorBatch}
            totalFailedBatchItems={failedCreateBatchItems.length}
            totalImportedRows={totalImportedRows}
            isUploadStarted={isBatchUploadStarted}
          />

          {isBatchUploadStarted ||
          bulkCollaboratorBatchQuery.data?.status === BulkStatus.Complete ? (
            <WSButton.Primary
              mt="L"
              className={styles.actionBtn}
              fullWidth
              name="continueToCalculationSettings"
              onClick={onContinue}
              loading={updateActivityMeta.isLoading}
              disabled={isBatchUploadStarted}
            >
              Continue to off-platform payments
            </WSButton.Primary>
          ) : (
            <WSButton.Primary
              mt="L"
              className={styles.actionBtn}
              fullWidth
              name="bulkUploadCollaborators"
              onClick={onConfirmImport}
              loading={
                isBatchUploadStarted ||
                updateBulkCollaboratorBatchMeta.isLoading
              }
            >
              Upload
            </WSButton.Primary>
          )}
        </WSPanel>
      )}
    </NEC1099FilingContainer>
  );
};
